import type { Translation } from '../types';

export const es: Translation = {
  meta: { name: 'Español', english: 'Spanish', tag: 'es', dir: 'ltr' },

  common: {
    menu: "Menú",
    cancel: 'Cancelar',
    done: 'Listo',
    join: 'Entrar',
    leave: 'Salir',
    refresh: 'Actualizar',
    save: 'Guardar',
    you: 'tú',
    host: 'anfitrión',
    ai: 'IA',
    ready: 'Listo',
    notReady: 'No listo',
  },

  conn: {
    live: 'Conectado',
    connecting: 'Conectando',
    offline: 'Sin conexión',
    connectingFull: 'Conectando…',
    reconnectBanner: 'Conexión perdida — reconectando. Tu sitio se guarda durante 3 minutos.',
  },

  nickname: {
    pickTitle: 'Elige un nombre',
    changeTitle: 'Cambiar tu nombre',
    subtitle: 'Así te verán los demás jugadores.',
    placeholder: 'p. ej. Comodín +4',
    label: 'Apodo',
    letsPlay: 'A jugar',
  },

  lobby: {
    heroTitle: 'Juega al UNO con amigos',
    heroSubtitle: 'Sin registro. Comparte un enlace y reparte.',
    createRoom: 'Crear sala',
    roomCode: 'Código de sala',
    publicRooms: 'Salas públicas',
    empty: 'Todavía no hay salas públicas — crea la primera.',
    cardMeta: 'anfitrión {host} · turnos de {seconds}s',
    stacking: 'acumulación',
    badgeOpen: 'Abierta',
    badgeFull: 'Llena',
    badgeLive: 'En partida',
    lockedTitle: 'La sala {id} está bloqueada',
    password: 'Contraseña',
    passwordLabel: 'Contraseña de la sala',
  },

  create: {
    title: 'Nueva sala',
    roomName: 'Nombre de la sala',
    roomNamePlaceholder: 'UNO del viernes',
    maxPlayers: 'Jugadores máx.',
    playersOption: { one: '{count} jugador', other: '{count} jugadores' },
    passwordOptional: 'Contraseña (opcional)',
    passwordPlaceholder: 'Déjalo vacío para sala abierta',
    listPublic: 'Mostrar en la lista de salas públicas',
    houseRules: 'Reglas de la casa',
    submit: 'Crear',
  },

  rules: {
    turnTimer: 'Tiempo por turno',
    turnTimerHint: 'Al agotarse, un bot juega por ti una carta válida al azar.',
    startingHand: 'Mano inicial',
    unoPenalty: 'Penalización UNO',
    stackDrawTwo: 'Acumular +2',
    stackDrawTwoHint: 'Responder a un +2 con otro +2 en vez de robar',
    stackDrawFour: 'Acumular +4',
    stackDrawFourHint: 'Responder a un +4 con otro +4',
    challengeWildFour: 'Desafiar el +4',
    challengeWildFourHint: 'El siguiente jugador puede denunciar un +4 ilegal',
    strictWildFour: '+4 estricto',
    strictWildFourHint: 'Solo se puede jugar si no tienes ninguna carta del color activo',
    playDrawn: 'Jugar lo que robas',
    playDrawnHint: 'La carta robada se puede jugar al momento',
    drawUntilPlayable: 'Robar hasta poder jugar',
    drawUntilPlayableHint: 'Seguir robando hasta conseguir una carta válida',
    hostOnly: 'solo el anfitrión puede cambiarlas',
  },

  room: {
    copyLink: 'Copiar enlace de invitación',
    copied: '✓ Copiado',
    roomLabel: 'Sala',
    locked: 'bloqueada',
    public: 'pública',
    unlisted: 'no listada',
    readyStatus: '{ready} de {total} listos',
    botsJoining: { one: 'se une {count} bot', other: 'se unen {count} bots' },
    startsWhenReady: 'la partida empieza en cuanto estén todos.',
    needMorePlayers: 'Hacen falta {min} jugadores para empezar — invita a alguien o añade un bot.',
    playersTitle: 'Jugadores',
    botsTitle: 'Bots IA',
    botsHostOnly: 'solo el anfitrión puede cambiarlo',
    noBots: 'Sin bots',
    botsWillJoin: { one: 'Se unirá {count} bot', other: 'Se unirán {count} bots' },
    botsHint: 'Se sientan al empezar la partida. Ahora mismo caben {max}.',
    botsRoomFull: 'La sala está llena — no hay sitio para bots.',
    soloCta: '¿Juegas solo? Añade un bot y empieza',
    addBot: 'Añadir un bot',
    removeBot: 'Quitar un bot',
    removePlayer: 'Expulsar a {name}',
    botPlaceholder: 'Bot {n}',
    defaultRoomName: 'Sala de {name}',
    imReady: 'Estoy listo',
    cancelReady: 'Cancelar listo',
    offlineTag: 'sin conexión',
  },

  game: {
    yourTurn: 'Tu turno',
    yourTurnStack: 'Tu turno — acumula o roba {count}',
    othersTurn: 'Turno de {name}',
    waiting: 'Esperando…',
    gameOver: 'Fin de la partida',
    challengeWindow: 'Comodín +4 — turno de desafío',
    drawPileLeft: 'quedan {count}',
    discard: 'descarte',
    draw: 'Robar',
    takeCards: 'Robar {count}',
    pass: 'Pasar',
    uno: '¡UNO!',
    judge: 'DENUNCIAR',
    cardCount: { one: '{count} carta', other: '{count} cartas' },
    clockwise: 'Sentido horario',
    counterClockwise: 'Sentido antihorario',
    activeColour: 'Color activo',
    noCards: 'Sin cartas — bien jugado.',
    oneCard: '¡una carta!',
    unoTag: 'UNO',
    forfeitedTag: 'abandonó',
    offlineFor: 'sin conexión {seconds}s',
    placeTag: '{place}º',
    pickColour: 'Elige un color',
    challengeTitle: '{name} ha jugado un Comodín +4. ¿Lo desafías?',
    challengeExplain: 'Si iba de farol, roba {bluff}. Si no, robas tú {fail}.',
    challengeBtn: 'Desafiar',
    acceptBtn: 'Robar {count}',
    youWin: '🎉 ¡Ganaste!',
    standingsTitle: 'Fin de la partida',
    points: '{points} pts',
    forfeited: 'abandonó',
    backToLobby: 'Volver al vestíbulo',
    playAgain: 'Jugar otra vez',
    waitingOthers: 'Esperando a los demás…',
    faceDownCard: 'Carta boca abajo',
    sortByColour: "Ordenar por color",
    sortAsDealt: "Mantener el orden repartido",
    drawACard: 'Robar una carta',
    drawNCards: 'Robar {count} cartas',
  },

  events: {
    someone: 'Alguien',
    unoCalledYou: '¡Has cantado UNO!',
    unoCalledOther: '¡{name} ha cantado UNO!',
    judged: '{accuser} pilló a {target} — +{count} cartas',
    judgedYou: '{accuser} te pilló — +{count} cartas',
    challengeBluff: 'Farol descubierto — {name} roba {count}',
    challengeFailed: 'Desafío fallido — {name} roba {count}',
    forfeited: '{name} ha abandonado.',
    afkYou: 'Se te acabó el tiempo — jugada automática.',
    afkOther: 'A {name} se le acabó el tiempo — jugada automática.',
    hostChanged: '{name} es ahora el anfitrión.',
    reshuffled: 'Mazo de robo rebarajado.',
    youWin: '¡Ganaste!',
    winner: '¡Gana {name}!',
    gameOver: 'Fin de la partida.',
  },

  errors: {
    bad_request: 'Algo ha salido mal.',
    no_session: 'Sesión caducada — reconectando.',
    room_not_found: 'Esa sala no existe.',
    room_full: 'Esa sala está llena.',
    wrong_password: 'Contraseña incorrecta.',
    already_in_room: 'Sal primero de tu sala actual.',
    not_in_room: 'No estás en ninguna sala.',
    not_host: 'Solo el anfitrión puede hacerlo.',
    game_in_progress: 'Esa partida ya ha empezado.',
    not_enough_players: 'No hay suficientes jugadores.',
    stale_action: 'Alguien se te adelantó — tu jugada se descartó.',
    illegal_action: 'Esa jugada no está permitida.',
    rate_limited: 'Más despacio.',
    nickname_invalid: 'Ese apodo no vale.',
  },

  theme: {
    title: 'Tema de cartas',
    description: 'Las imágenes que falten usan la baraja CSS integrada.',
    builtIn: 'Integrado — no requiere archivos',
    pngHint: 'Pon PNG de 300×450 en {path} y regístralos en el índice de temas.',
  },

  language: {
    title: 'Idioma',
    description: 'Se aplica al instante y se recuerda en este dispositivo.',
  },

  cards: {
    red: "rojo",
    yellow: "amarillo",
    green: "verde",
    blue: "azul",
    wild: "comodín",
    skip: "Salta",
    reverse: "Cambio",
    draw2: "+2",
    wildCard: "Comodín",
    wild4: "+4",
  },

  log: {
    title: "Registro",
    empty: "Nada todavía.",
    close: "Ocultar registro",
    timestamps: "Marcas de tiempo",
    chatPlaceholder: "Di algo…",
    send: "Enviar",
    played: "{name} juega {card}",
    drew: "{name} roba {count}",
    skipped: "{name} pierde el turno",
    reversed: "Sentido invertido",
    joined: "{name} ha entrado",
    left: "{name} ha salido",
    started: "Partida iniciada",
  },

  profile: {
    avatar: "Avatar",
    avatarHint: "Elige un avatar, o quédate con tu inicial.",
  },

  leave: {
    title: "¿Salir de la partida?",
    body: "La ronda sigue en marcha. Si sales, abandonas y tus cartas vuelven al mazo.",
    stay: "Seguir jugando",
    confirm: "Salir igualmente",
    unload: "Estás en mitad de una partida.",
  },
};
